import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Loader2, FileSearch, RefreshCw, ShieldAlert, TrendingUp, TrendingDown, Wallet,
  Activity, Banknote, Building2, CheckCircle2, XCircle, Upload, Gauge, Landmark,
  ArrowDownLeft, ArrowUpRight, AlertTriangle, ReceiptText, BadgeCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { loanProfilingApi } from '@/services/api';
import { SectionHeading } from './CockpitUI';

const reveal = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } };

const inr = (n) => {
  if (n == null || isNaN(Number(n))) return '₹0';
  const num = Number(n);
  return `${num < 0 ? '-' : ''}₹${Math.abs(num).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
const pctTxt = (n) => `${(Number(n) || 0).toFixed(2)}%`;
const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = String(m).split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return mo ? `${names[parseInt(mo, 10) - 1] || mo} ${String(y).slice(2)}` : m;
};

// ── small UI atoms ───────────────────────────────────────────────
const Card = ({ title, icon: Icon, children, className = '', testId }) => (
  <div className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${className}`} data-testid={testId}>
    {title && (
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
      </div>
    )}
    {children}
  </div>
);

const KV = ({ label, value, valueClass = 'text-slate-900', highlight }) => (
  <div className={`flex items-center justify-between py-2.5 ${highlight ? 'px-3 -mx-1 rounded-lg' : ''}`}
    style={highlight ? { background: highlight } : undefined}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm font-semibold ${valueClass}`}>{value}</span>
  </div>
);

const Stat = ({ label, value, sub, tone = 'slate', icon: Icon, testId }) => {
  const tones = {
    slate: 'border-slate-200', green: 'border-l-emerald-500', red: 'border-l-rose-500',
    blue: 'border-l-blue-500', amber: 'border-l-amber-500', violet: 'border-l-violet-500',
  };
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${tones[tone]} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-300" />}
      </div>
      <div className="text-lg font-bold text-slate-900 mt-1.5 leading-tight">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
};

const ModeRow = ({ mode, count, amount, share, color = 'bg-blue-500' }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
    <span className={`w-2 h-2 rounded-full ${color} shrink-0`} />
    <span className="text-sm text-slate-700 flex-1 truncate capitalize">{(mode || '').toLowerCase()}</span>
    <div className="flex items-center gap-4 text-xs text-slate-500 shrink-0">
      <span className="w-10 text-right">{count ?? 0}</span>
      <span className="w-20 text-right font-semibold text-slate-700">{inr(amount)}</span>
      <span className="w-12 text-right">{pctTxt(share)}</span>
    </div>
  </div>
);

const TxnRank = ({ rank, name, date, mode, amount, share, credit }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center shrink-0">{rank}</span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-slate-800 truncate">{name || '—'}</div>
      <div className="text-xs text-slate-400 truncate">{date} • {(mode || '').toLowerCase()}</div>
    </div>
    <div className="text-right shrink-0">
      <div className={`text-sm font-semibold ${credit ? 'text-emerald-600' : 'text-rose-600'}`}>{credit ? '+' : '-'}{inr(amount)}</div>
      <div className="text-xs text-slate-400">{pctTxt(share)}</div>
    </div>
  </div>
);

// ── tampering banner ─────────────────────────────────────────────
const TamperBanner = ({ t }) => {
  if (!t || !t.checked) return null;
  const high = t.risk_level === 'High';
  const med = t.risk_level === 'Medium';
  if (!t.is_tampered) {
    return (
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-5" data-testid="bsa-tamper-banner">
        <BadgeCheck className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-medium text-emerald-800">No PDF tampering detected</span>
        <span className="text-xs text-emerald-600 ml-auto">Producer: {t.producer || '-'}</span>
      </div>
    );
  }
  return (
    <div className={`rounded-xl p-5 mb-5 border ${high ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`} data-testid="bsa-tamper-banner">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className={`w-4 h-4 ${high ? 'text-rose-600' : 'text-amber-600'}`} />
        <span className={`text-sm font-bold ${high ? 'text-rose-800' : 'text-amber-800'}`}>PDF Tampering Detected</span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${high ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>Risk: {t.risk_level}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        {[['Risk Level', t.risk_level], ['Author', t.author], ['Creator', t.creator], ['Producer', t.producer]].map(([k, v]) => (
          <div key={k}>
            <div className="text-[11px] uppercase tracking-wider text-slate-400">{k}</div>
            <div className="text-sm font-medium text-slate-700 truncate">{v || '-'}</div>
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        {(t.warnings || []).map((w, i) => (
          <div key={i} className="flex gap-2 text-xs">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <span><span className="font-semibold text-slate-700">{w.title}.</span> <span className="text-slate-500">{w.detail}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── dashboard ────────────────────────────────────────────────────
const Dashboard = ({ a }) => {
  const bs = a.balance_summary || {};
  const ts = a.transaction_summary || {};
  const ki = a.key_indicators || {};
  const abb = a.average_bank_balance || {};
  const bnc = a.bounce_summary || {};
  const sal = a.salary_analysis || {};
  const emi = a.emi_obligations || {};
  const sus = a.suspicious || {};
  const charges = a.bank_charges || {};
  const cashflow = (a.monthly_cash_flow || []).map((m) => ({ name: monthLabel(m.month), Credits: Number(m.credits) || 0, Debits: -(Math.abs(Number(m.debits) || 0)) }));
  const avgMonthly = ts.total_credits_amount && a.months_analyzed ? Math.round(ts.total_credits_amount / a.months_analyzed) : (ki.monthly_salary || 0);

  return (
    <Tabs defaultValue="overview" className="w-full" data-testid="bsa-dashboard">
      <TabsList className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 mb-5">
        <TabsTrigger value="overview" data-testid="bsa-tab-overview"><Activity className="w-3.5 h-3.5 mr-1.5" />Overview</TabsTrigger>
        <TabsTrigger value="salary" data-testid="bsa-tab-salary"><Banknote className="w-3.5 h-3.5 mr-1.5" />Salary Analysis</TabsTrigger>
        <TabsTrigger value="emi" data-testid="bsa-tab-emi"><Landmark className="w-3.5 h-3.5 mr-1.5" />EMI &amp; Obligations</TabsTrigger>
        <TabsTrigger value="suspicious" data-testid="bsa-tab-suspicious"><ShieldAlert className="w-3.5 h-3.5 mr-1.5" />Suspicious</TabsTrigger>
        <TabsTrigger value="top" data-testid="bsa-tab-top"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Top Transactions</TabsTrigger>
        <TabsTrigger value="charges" data-testid="bsa-tab-charges"><ReceiptText className="w-3.5 h-3.5 mr-1.5" />Bank Charges</TabsTrigger>
      </TabsList>

      {/* OVERVIEW */}
      <TabsContent value="overview">
        <motion.div {...reveal} className="space-y-5">
          {/* account header */}
          <Card testId="bsa-account-header">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center text-sm shrink-0">
                {(a.account_holder_name || 'NA').split(' ').map((w) => w[0]).slice(0, 2).join('')}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900">{a.account_holder_name || '—'}</div>
                <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${a.employment_type === 'Salaried' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {a.employment_type || 'N/A'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
              {[['Bank Name', a.bank_name], ['Account', a.account_number_masked], ['Period', `${a.statement_period_from || ''} – ${a.statement_period_to || ''}`], ['IFSC', a.ifsc_code]].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400">{k}</div>
                  <div className="text-sm font-medium text-slate-700 truncate">{v || '—'}</div>
                </div>
              ))}
            </div>
          </Card>

          <TamperBanner t={a.pdf_tampering} />

          {/* summary cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card title="Balance Summary" icon={Wallet}>
              <KV label="Opening Balance" value={inr(bs.opening_balance)} />
              <KV label="Closing Balance" value={inr(bs.closing_balance)} valueClass="text-emerald-600" />
              <KV label="Average Balance" value={inr(bs.average_balance)} highlight="#eff6ff" />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="bg-emerald-50 rounded-lg p-3"><div className="text-xs text-slate-500">Max</div><div className="text-sm font-bold text-emerald-700">{inr(bs.max_balance)}</div></div>
                <div className="bg-rose-50 rounded-lg p-3"><div className="text-xs text-slate-500">Min</div><div className="text-sm font-bold text-rose-700">{inr(bs.min_balance)}</div></div>
              </div>
            </Card>
            <Card title="Transaction Summary" icon={Activity}>
              <KV label="Total Transactions" value={ts.total_transactions ?? 0} />
              <KV label={`Total Credits (${ts.total_credits_count ?? 0})`} value={inr(ts.total_credits_amount)} valueClass="text-emerald-600" />
              <KV label={`Total Debits (${ts.total_debits_count ?? 0})`} value={inr(ts.total_debits_amount)} valueClass="text-rose-600" />
              <KV label="Net Cash Flow" value={inr(ts.net_cash_flow)} valueClass={Number(ts.net_cash_flow) >= 0 ? 'text-emerald-600' : 'text-rose-600'} highlight="#fffbeb" />
            </Card>
            <Card title="Key Indicators" icon={Gauge}>
              <KV label="Monthly Salary" value={inr(ki.monthly_salary)} valueClass="text-blue-600" />
              <KV label="Active EMIs" value={`${ki.active_emis_count ?? 0} (${inr(ki.active_emis_amount)})`} />
              <KV label="Suspicious Txns" value={ki.suspicious_txn_count ?? 0} valueClass={(ki.suspicious_txn_count ?? 0) > 0 ? 'text-rose-600' : 'text-slate-900'} />
              <KV label="EMI Bounces" value={ki.emi_bounce_count ?? 0} valueClass={(ki.emi_bounce_count ?? 0) > 0 ? 'text-rose-600' : 'text-slate-900'} />
            </Card>
          </div>

          {/* avg balance + bounce */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Average Bank Balance" icon={TrendingUp}>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3"><div className="text-xs text-slate-500">Last 30 Days</div><div className="text-sm font-bold text-blue-700">{inr(abb.last_30_days)}</div></div>
                <div className="bg-blue-50 rounded-lg p-3"><div className="text-xs text-slate-500">Last 3 Months</div><div className="text-sm font-bold text-blue-700">{inr(abb.last_3_months)}</div></div>
                <div className="bg-violet-50 rounded-lg p-3"><div className="text-xs text-slate-500">Last 6 Months</div><div className="text-sm font-bold text-violet-700">{inr(abb.last_6_months)}</div></div>
                <div className="bg-cyan-50 rounded-lg p-3"><div className="text-xs text-slate-500">Forecast (30d)</div><div className="text-sm font-bold text-cyan-700">{inr(abb.forecast_30d)}</div></div>
              </div>
              <KV label="Net Balance Change" value={inr(abb.net_balance_change)} valueClass={Number(abb.net_balance_change) >= 0 ? 'text-emerald-600' : 'text-rose-600'} />
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><div className="text-xs text-slate-400">Peak Month</div><div className="text-sm font-semibold text-emerald-600">{abb.peak_month || '—'}</div></div>
                <div><div className="text-xs text-slate-400">Trough Month</div><div className="text-sm font-semibold text-rose-600">{abb.trough_month || '—'}</div></div>
                <div><div className="text-xs text-slate-400">AMB Charges</div><div className="text-sm font-semibold text-amber-600">{inr(abb.amb_charges)}</div></div>
              </div>
            </Card>
            <Card title="Bounce Summary" icon={AlertTriangle}>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-rose-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Inward</div><div className="text-lg font-bold text-rose-600">{bnc.inward_bounce_count ?? 0}</div></div>
                <div className="bg-amber-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Outward</div><div className="text-lg font-bold text-amber-600">{bnc.outward_bounce_count ?? 0}</div></div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Auto Debit</div><div className="text-lg font-bold text-yellow-600">{bnc.auto_debit_bounce_count ?? 0}</div></div>
              </div>
              <KV label="Total Bounce Amount" value={inr(bnc.total_bounce_amount)} valueClass={(bnc.total_bounce_amount ?? 0) > 0 ? 'text-rose-600' : 'text-slate-900'} />
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-3 mb-1">Cheque Returns</div>
              <div className="grid grid-cols-2 gap-3">
                <KV label="Inward — 6M" value={bnc.cheque_returns_inward_6m ?? 0} />
                <KV label="Outward — 6M" value={bnc.cheque_returns_outward_6m ?? 0} />
              </div>
            </Card>
          </div>

          {/* metric strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Stat label="Total Credits" tone="green" icon={ArrowUpRight} value={inr(ts.total_credits_amount)} sub="All months" />
            <Stat label="Total Debits" tone="red" icon={ArrowDownLeft} value={inr(ts.total_debits_amount)} sub="All months" />
            <Stat label="Transactions" tone="blue" icon={Activity} value={ts.total_transactions ?? 0} sub="All months" />
            <Stat label="Net Flow" tone={Number(ts.net_cash_flow) >= 0 ? 'green' : 'red'} icon={TrendingUp} value={inr(ts.net_cash_flow)} sub="All months" />
            <Stat label="Avg Monthly" tone="violet" icon={Wallet} value={inr(avgMonthly)} sub="Credits" />
          </div>

          {/* cash flow chart */}
          {cashflow.length > 0 && (
            <Card title="Monthly Cash Flow" icon={Activity} testId="bsa-cashflow">
              <p className="text-xs text-slate-400 -mt-3 mb-3">Credits (positive) vs Debits (negative)</p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={cashflow} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Bar dataKey="Credits" radius={[4, 4, 0, 0]}>{cashflow.map((e, i) => <Cell key={i} fill="#10b981" />)}</Bar>
                  <Bar dataKey="Debits" radius={[0, 0, 4, 4]}>{cashflow.map((e, i) => <Cell key={i} fill="#ef4444" />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* inflow / outflow */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Inflow Details" icon={ArrowUpRight}>
              <div className="flex items-center justify-end gap-4 text-[11px] uppercase tracking-wider text-slate-400 pb-1 border-b border-slate-100">
                <span className="w-10 text-right">Txns</span><span className="w-20 text-right">Amount</span><span className="w-12 text-right">Share</span>
              </div>
              {(a.inflow_details || []).map((m, i) => <ModeRow key={i} {...m} share={m.share_pct} color="bg-emerald-500" />)}
              {(a.inflow_details || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No inflow breakdown</p>}
            </Card>
            <Card title="Outflow Details" icon={ArrowDownLeft}>
              <div className="flex items-center justify-end gap-4 text-[11px] uppercase tracking-wider text-slate-400 pb-1 border-b border-slate-100">
                <span className="w-10 text-right">Txns</span><span className="w-20 text-right">Amount</span><span className="w-12 text-right">Share</span>
              </div>
              {(a.outflow_details || []).map((m, i) => <ModeRow key={i} {...m} share={m.share_pct} color="bg-rose-500" />)}
              {(a.outflow_details || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No outflow breakdown</p>}
            </Card>
          </div>

          {a.analysis_notes && (
            <Card title="Underwriter Notes" icon={FileSearch}>
              <p className="text-sm text-slate-600 leading-relaxed">{a.analysis_notes}</p>
              <p className="text-xs text-slate-400 mt-2">AI confidence: {a.confidence_score ?? '—'}%</p>
            </Card>
          )}
        </motion.div>
      </TabsContent>

      {/* SALARY */}
      <TabsContent value="salary">
        <motion.div {...reveal} className="space-y-5">
          <div className={`rounded-xl px-4 py-3 flex items-center gap-2 border ${sal.salary_detected ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            {sal.salary_detected ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-amber-600" />}
            <span className={`text-sm font-semibold ${sal.salary_detected ? 'text-emerald-800' : 'text-amber-800'}`}>
              {sal.salary_detected ? 'Consistent Salary Detected' : 'No Consistent Salary Detected'}
            </span>
            {sal.employer && <span className="text-xs font-medium text-slate-600 ml-auto truncate max-w-[50%]">{sal.employer}</span>}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat label="Monthly Salary" tone="green" value={inr(sal.monthly_amount)} sub={sal.employer || 'N/A'} />
            <Stat label="Months w/ Salary" tone="blue" value={sal.months_with_salary ?? sal.payment_count ?? 0} sub="With a credit" />
            <Stat label="Salary Payments" tone="violet" value={sal.payment_count ?? 0} sub="Detected" />
            <Stat label="Credit Day" tone="amber" value={sal.credit_day ?? 'N/A'} sub={sal.credit_day_range ? `Range ${sal.credit_day_range}` : 'Of month'} />
            <Stat label="Confidence" tone="green" value={`${sal.confidence_pct ?? 0}%`} sub={`Variance ${sal.variance_pct ?? 0}%`} />
          </div>
          <Card title="Consistency Checks" icon={BadgeCheck}>
            <div className="flex flex-wrap gap-2">
              {[['Same Employer', sal.source_consistent], ['Consistent Amount', sal.amount_consistent], ['Consistent Date', sal.date_consistent], ['Salary Keyword Found', sal.salary_keyword_found]].map(([label, ok]) => (
                <span key={label} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                  {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{label}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">A strong salary signal needs the same employer paying a similar amount on a similar date across 3+ months.</p>
          </Card>
          <Card title="Salary Credits" icon={Banknote}>
            {(() => {
              const credits = (sal.monthly_salaries && sal.monthly_salaries.length) ? sal.monthly_salaries : (sal.transactions || []);
              if (credits.length === 0) return <p className="text-sm text-slate-400 py-6 text-center">No salary credits found</p>;
              return credits.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700 truncate">{t.description || t.month}</div>
                    <div className="text-xs text-slate-400">{t.date || t.month}{t.month && t.date ? ` • ${monthLabel(t.month)}` : ''}</div>
                  </div>
                  <span className="text-sm font-semibold text-emerald-600 shrink-0">+{inr(t.amount)}</span>
                </div>
              ));
            })()}
          </Card>
        </motion.div>
      </TabsContent>

      {/* EMI */}
      <TabsContent value="emi">
        <motion.div {...reveal} className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat label="Monthly Burden" tone="red" value={inr(emi.monthly_burden)} sub="Per month" />
            <Stat label="Total EMIs" tone="blue" value={emi.total_emis ?? 0} sub="Active loans" />
            <Stat label="Total Bounces" tone="amber" value={emi.total_bounces ?? 0} sub="Failed payments" />
            <Stat label="Risk Score" tone="violet" value={`${emi.risk_score ?? 0}`} sub="Out of 100" />
            <Stat label="Risk Category" tone={emi.risk_category === 'HIGH' ? 'red' : emi.risk_category === 'MEDIUM' ? 'amber' : 'green'} value={emi.risk_category || 'LOW'} sub="Assessment" />
          </div>
          <Card title="Risk Indicators" icon={ShieldAlert}>
            <div className="flex flex-wrap gap-2">
              {[['Multiple EMIs', 'multiple_emis'], ['High EMI Burden', 'high_emi_burden'], ['Irregular EMI', 'irregular_emi'], ['EMI Bounces', 'emi_bounces'], ['High Bounce Rate', 'high_bounce_rate']].map(([label, key]) => {
                const on = (emi.risk_indicators || {})[key];
                return (
                  <span key={key} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${on ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                    {on ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}{label}
                  </span>
                );
              })}
            </div>
          </Card>
          <Card title={`Lender Details (${(emi.lenders || []).length})`} icon={Landmark}>
            {(emi.lenders || []).length === 0
              ? <div className="text-center py-8"><CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300 mb-2" /><p className="text-sm font-medium text-slate-600">No EMI Obligations</p><p className="text-xs text-slate-400">No active loans or EMI payments detected</p></div>
              : (emi.lenders || []).map((l, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3"><Building2 className="w-4 h-4 text-slate-400" /><span className="text-sm font-medium text-slate-700">{l.lender}</span></div>
                  <div className="text-right"><div className="text-sm font-semibold text-slate-800">{inr(l.emi_amount)}</div><div className="text-xs text-slate-400">{l.count} payment(s)</div></div>
                </div>
              ))}
          </Card>
        </motion.div>
      </TabsContent>

      {/* SUSPICIOUS */}
      <TabsContent value="suspicious">
        <motion.div {...reveal} className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Total Suspicious" tone="red" value={sus.total_suspicious ?? 0} />
            <Stat label="Suspicious Credits" tone="amber" value={sus.suspicious_credits_count ?? 0} />
            <Stat label="Suspicious Debits" tone="red" value={sus.suspicious_debits_count ?? 0} />
          </div>
          {(sus.total_suspicious ?? 0) === 0 ? (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <CheckCircle2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">All Clear — no suspicious transactions detected in the statement</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card title="Suspicious Credits" icon={ArrowUpRight}>
                {(sus.suspicious_credits || []).map((t, i) => (
                  <div key={i} className="py-2.5 border-b border-slate-100 last:border-0">
                    <div className="flex justify-between"><span className="text-sm font-medium text-slate-700 truncate">{t.description}</span><span className="text-sm font-semibold text-emerald-600">+{inr(t.amount)}</span></div>
                    <div className="text-xs text-slate-400">{t.date} • {t.reason}</div>
                  </div>
                ))}
              </Card>
              <Card title="Suspicious Debits" icon={ArrowDownLeft}>
                {(sus.suspicious_debits || []).map((t, i) => (
                  <div key={i} className="py-2.5 border-b border-slate-100 last:border-0">
                    <div className="flex justify-between"><span className="text-sm font-medium text-slate-700 truncate">{t.description}</span><span className="text-sm font-semibold text-rose-600">-{inr(t.amount)}</span></div>
                    <div className="text-xs text-slate-400">{t.date} • {t.reason}</div>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </motion.div>
      </TabsContent>

      {/* TOP TRANSACTIONS */}
      <TabsContent value="top">
        <motion.div {...reveal} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Top Credits" icon={TrendingUp}>
            <p className="text-xs text-slate-400 -mt-3 mb-2">Highest value credit transactions</p>
            {(a.top_credits || []).map((t, i) => <TxnRank key={i} {...t} share={t.share_pct} credit />)}
            {(a.top_credits || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No data</p>}
          </Card>
          <Card title="Top Debits" icon={TrendingDown}>
            <p className="text-xs text-slate-400 -mt-3 mb-2">Highest value debit transactions</p>
            {(a.top_debits || []).map((t, i) => <TxnRank key={i} {...t} share={t.share_pct} />)}
            {(a.top_debits || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No data</p>}
          </Card>
        </motion.div>
      </TabsContent>

      {/* BANK CHARGES */}
      <TabsContent value="charges">
        <motion.div {...reveal} className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Status" tone={charges.status === 'No Charges' ? 'green' : 'amber'} value={charges.status || 'No Charges'} sub={charges.status === 'No Charges' ? 'Clean account' : 'Charges found'} />
            <Stat label="Total Transactions" tone="blue" value={charges.total_transactions ?? 0} sub="Charge entries" />
            <Stat label="Last 6 Months" tone="amber" value={inr(charges.last_6_months_amount)} sub="Charges" />
            <Stat label="Last 12 Months" tone="violet" value={inr(charges.last_12_months_amount)} sub="Charges" />
          </div>
          <Card title="Charge Transactions" icon={ReceiptText}>
            {(charges.transactions || []).length === 0
              ? <p className="text-sm text-slate-400 py-6 text-center">No charge transactions</p>
              : (charges.transactions || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                  <div className="min-w-0"><div className="text-sm font-medium text-slate-700 truncate">{t.description}</div><div className="text-xs text-slate-400">{t.date}</div></div>
                  <span className="text-sm font-semibold text-amber-600 shrink-0">{inr(t.amount)}</span>
                </div>
              ))}
          </Card>
        </motion.div>
      </TabsContent>
    </Tabs>
  );
};

// ── main ─────────────────────────────────────────────────────────
export const BankStatementAnalysis = ({ leadId, income, onChanged }) => {
  const [status, setStatus] = useState('loading'); // loading|none|ready|processing|done|failed|stalled
  const [analysis, setAnalysis] = useState(null);
  const [analyzedAt, setAnalyzedAt] = useState(null);
  const [hasStatement, setHasStatement] = useState(!!income?.bank_statement_doc_id);
  const [stale, setStale] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await loanProfilingApi.getBankStatementAnalysis(leadId);
      const d = res.data || {};
      setHasStatement(!!d.has_statement);
      setStale(!!d.stale);
      setAnalyzedAt(d.analyzed_at);
      if (d.elapsed_seconds != null) setElapsed(d.elapsed_seconds);
      if (d.status === 'done' && d.analysis?.success) { setAnalysis(d.analysis); setStatus('done'); }
      else if (d.status === 'processing') { setStatus('processing'); }
      else if (d.status === 'stalled') { setAnalysis(d.analysis); setStatus('stalled'); }
      else if (d.status === 'failed') { setAnalysis(d.analysis); setStatus('failed'); }
      else { setStatus(d.has_statement ? 'ready' : 'none'); }
      return d.status;
    } catch (e) { setStatus('none'); return 'none'; }
  }, [leadId]);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  // Keep polling for as long as the backend says the run is alive, and pick the
  // result up the moment the rep comes back to the tab.
  useEffect(() => {
    if (status !== 'processing') return undefined;
    let ticks = 0;
    const tick = async () => {
      ticks += 1;
      const s = await load();
      if (s !== 'processing') {
        clearInterval(pollRef.current);
        if (onChanged) onChanged();
      } else if (ticks === 30) {
        // ease off after the first two minutes instead of giving up
        clearInterval(pollRef.current);
        pollRef.current = setInterval(tick, 10000);
      }
    };
    pollRef.current = setInterval(tick, 4000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [status, load, onChanged]);

  const runAnalysis = async () => {
    try {
      await loanProfilingApi.startBankStatementAnalysis(leadId);
      setElapsed(0);
      setStatus('processing');
      toast.info('Analyzing bank statement… this can take up to a minute.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start analysis');
    }
  };

  const pickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      toast.error('Please upload a PDF bank statement'); return;
    }
    setUploading(true);
    try {
      const up = await loanProfilingApi.uploadDocument(leadId, 'bank_statement', file);
      const doc = up.data?.document || up.data;
      const docId = doc?.id;
      if (!docId) throw new Error('Upload failed');
      await loanProfilingApi.saveIncomeDetails(leadId, { bank_statement_doc_id: docId, bank_statement_doc_name: file.name });
      setHasStatement(true);
      await loanProfilingApi.startBankStatementAnalysis(leadId, { doc_id: docId });
      setAnalysis(null);
      setElapsed(0);
      setStatus('processing');
      toast.success('New bank statement uploaded — analyzing…');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload statement');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="bsa-root">
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFileChange} data-testid="bsa-upload-input" />
      <div className="flex items-start justify-between gap-3 mb-5">
        <SectionHeading hint="Every figure here is read from the statement PDF itself; the AI only adds the commentary.">
          Bank Statement Analysis
        </SectionHeading>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={pickFile} disabled={uploading} data-testid="bsa-upload-new-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            {uploading ? 'Uploading…' : 'Upload new statement'}
          </Button>
          {(status === 'done' || status === 'failed' || status === 'stalled') && (
            <Button variant="outline" size="sm" onClick={runAnalysis} data-testid="bsa-reanalyze-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Re-analyze
            </Button>
          )}
        </div>
      </div>

      {status === 'loading' && (
        <div className="flex items-center justify-center py-24" data-testid="bsa-loading"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
      )}

      {status === 'none' && (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50" data-testid="bsa-empty">
          <Upload className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="font-medium text-slate-600">No bank statement uploaded</p>
          <p className="text-sm text-slate-500 mb-4">Upload it here, or under <span className="font-medium">Customer Details → Income details → “Last 6 Months Bank Statement”</span>.</p>
          <Button onClick={pickFile} disabled={uploading} className="bg-blue-600 hover:bg-blue-700" data-testid="bsa-empty-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading…' : 'Upload statement'}
          </Button>
        </div>
      )}

      {(status === 'ready' || status === 'failed' || status === 'stalled') && (
        <div className="text-center py-14 border border-dashed border-slate-200 rounded-xl bg-white" data-testid="bsa-cta">
          <FileSearch className="w-12 h-12 mx-auto text-blue-300 mb-3" />
          <p className="font-medium text-slate-700">
            {status === 'failed' ? 'Analysis failed — try again'
              : status === 'stalled' ? 'The last run stopped responding — run it again'
                : 'Bank statement ready for analysis'}
          </p>
          {status === 'failed' && analysis?.error && <p className="text-xs text-rose-500 mt-1 max-w-md mx-auto">{String(analysis.error).slice(0, 160)}</p>}
          {status === 'stalled' && <p className="text-xs text-amber-600 mt-1 max-w-md mx-auto" data-testid="bsa-stalled-note">Nothing came back from the reader. Your statement is still on file — just run it again.</p>}
          <p className="text-sm text-slate-500 mt-1 mb-4">Run a banker-grade AI analysis: balances, salary, EMIs, bounces, suspicious activity, top transactions &amp; PDF tampering.</p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={runAnalysis} className="bg-blue-600 hover:bg-blue-700" data-testid="bsa-analyze-btn">
              <FileSearch className="w-4 h-4 mr-2" />Analyze Statement
            </Button>
            <Button variant="outline" onClick={pickFile} disabled={uploading} data-testid="bsa-cta-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload a different statement
            </Button>
          </div>
        </div>
      )}

      {status === 'processing' && (
        <div className="text-center py-16 border border-blue-100 rounded-xl bg-blue-50/40" data-testid="bsa-processing">
          <Loader2 className="w-10 h-10 mx-auto text-blue-600 animate-spin mb-3" />
          <p className="font-medium text-slate-700">Analyzing bank statement…</p>
          <p className="text-sm text-slate-500">
            {elapsed > 90
              ? 'Still reading — a long statement can take a few minutes. This keeps checking on its own.'
              : 'Reading the transactions — this usually takes 30–60 seconds.'}
          </p>
          <p className="text-xs text-slate-400 mt-1" data-testid="bsa-elapsed">
            {elapsed ? `Running for ${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : 'Just started'}
          </p>
          <Button variant="outline" size="sm" onClick={load} className="mt-4" data-testid="bsa-check-now-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Check now
          </Button>
        </div>
      )}

      {status === 'done' && analysis && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
            {analyzedAt && <p className="text-xs text-slate-400">Analyzed {new Date(analyzedAt).toLocaleString('en-IN')}</p>}
            {analysis.figures_source === 'statement_parse' ? (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5" data-testid="bsa-source-parsed">
                Figures read from the statement · {Number(analysis.transactions_parsed || 0).toLocaleString('en-IN')} transactions
                {analysis.pages_total ? ` · ${analysis.pages_read}/${analysis.pages_total} pages` : ''}
              </p>
            ) : (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="bsa-source-estimate">
                AI estimate — this statement's layout could not be read line by line. Verify the figures before deciding.
              </p>
            )}
            {stale && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="bsa-stale-note">
                A newer statement is on file — re-analyze to refresh
              </p>
            )}
          </div>
          <Dashboard a={analysis} />
        </>
      )}
    </div>
  );
};

export default BankStatementAnalysis;
